import { Injectable } from "@angular/core";

import { CryptoService } from "@bitwarden/common/abstractions/crypto.service";
import { CryptoFunctionService } from "@bitwarden/common/abstractions/cryptoFunction.service";
import { EncryptService } from "@bitwarden/common/abstractions/encrypt.service";
import { EncArrayBuffer } from "@bitwarden/common/models/domain/enc-array-buffer";
import { SymmetricCryptoKey } from "@bitwarden/common/models/domain/symmetric-crypto-key";

@Injectable({
  providedIn: "root",
})
export class FileEncryptionService {
  constructor(
    private cryptoService: CryptoService,
    private cryptoFunctionService: CryptoFunctionService,
    private encryptService: EncryptService,
  ) {}

  // The key is returned alongside the buffer rather than stashed on instance state, so a
  // concurrent encrypt() call on this shared singleton can't overwrite the key a caller is
  // about to persist against its own content (see gloss-client#3954, the create-path
  // counterpart to the buffer race fixed for replaceFile in #3699/#3700).
  async encrypt(file: File): Promise<{ key: SymmetricCryptoKey; encArrayBuffer: EncArrayBuffer }> {
    const key = await this.generateKey();
    const encArrayBuffer = await this.encryptContent(file, key);
    return { key, encArrayBuffer };
  }

  /**
   * Re-encrypts `file` under an already-existing key instead of generating a new one.
   * Used when replacing a vault file's content so the vault's stored key never changes -
   * if the follow-up metadata save fails, the content already written to storage under
   * this same key remains decryptable (see gloss-client#3699).
   */
  async encryptWith(file: File, key: SymmetricCryptoKey): Promise<EncArrayBuffer> {
    return this.encryptContent(file, key);
  }

  // Returned directly rather than stashed on instance state, so a concurrent encrypt()/
  // encryptWith() call on this shared singleton can't overwrite the buffer a caller is
  // about to upload (see gloss-client#3699/#3700).
  private async encryptContent(file: File, key: SymmetricCryptoKey): Promise<EncArrayBuffer> {
    const fileBuffer = await file.arrayBuffer();
    return this.encryptService.encryptToBytes(fileBuffer, key);
  }

  // Returned directly rather than stashed on instance state, so a concurrent decrypt() on this
  // shared singleton can't hand a caller another file's plaintext, and so decrypted plaintext
  // isn't retained on this root-scoped service for the rest of the app's lifetime
  // (see gloss-client#4179).
  async decrypt(encArrayBuffer: EncArrayBuffer, encryptedKey: SymmetricCryptoKey): Promise<File> {
    const decryptedFileData = await this.encryptService.decryptToBytes(
      encArrayBuffer,
      encryptedKey,
    );
    // decryptToBytes() signals failure (a MAC mismatch, an encType mismatch, or an internal
    // aesDecrypt failure) by resolving null rather than throwing (#4999). Without this guard,
    // `new Blob([null])` silently produces a 4-byte "null" body that renders as a successful,
    // corrupt file preview instead of surfacing the existing notifyError() path.
    if (decryptedFileData == null) {
      throw new Error("Failed to decrypt file");
    }
    const blob = new Blob([decryptedFileData]);
    return new File([blob], "decryptedFile");
  }

  private async generateKey(): Promise<SymmetricCryptoKey> {
    const key = await this.cryptoFunctionService.randomBytes(16);
    return this.cryptoService.makeSendKey(key);
  }
}
